import React, { useEffect } from 'react';
import type { Performer } from './PerformersCard';

const getRankColor = (rank: string) => {
  switch (rank) {
    case 'GrandMaster': return { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' };
    case 'Master': return { bg: 'rgba(168, 85, 247, 0.2)', color: '#a855f7' };
    case 'Expert': return { bg: 'rgba(96, 165, 250, 0.2)', color: '#60a5fa' };
    case 'Professional': return { bg: 'rgba(250, 204, 21, 0.2)', color: '#facc15' };
    case 'Apprentice': return { bg: 'rgba(156, 163, 175, 0.2)', color: '#9ca3af' };
    case 'Beginner': return { bg: 'rgba(217, 119, 6, 0.2)', color: '#d97706' };
    default: return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };
  }
};


interface TopPerformersModalProps {
  mode: string;
  performers: Performer[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onViewPlayer?: (userId: string) => void;
}

export const TopPerformersModal: React.FC<TopPerformersModalProps> = ({
  mode,
  performers,
  loading,
  error,
  onClose,
  onViewPlayer,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const styles = {
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      background: '#1e293b',
      borderRadius: '12px',
      width: '480px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column' as const,
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      borderBottom: '1px solid #334155',
    },
    title: {
      color: '#fff',
      fontSize: '16px',
      fontWeight: 700,
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: '#94a3b8',
      fontSize: '20px',
      cursor: 'pointer',
      padding: '4px 8px',
      lineHeight: 1,
    },
    body: {
      flex: 1,
      overflow: 'auto',
      padding: '12px 20px 20px',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 12px',
      margin: '4px 0',
      background: '#fff',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'background 0.15s',
    },
    rank: {
      width: '28px',
      fontSize: '14px',
      fontWeight: 700,
      color: '#6b7280',
      flexShrink: 0,
    },
    rankBadge: {
      padding: '4px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: 600,
      marginRight: '10px',
      flexShrink: 0,
      whiteSpace: 'nowrap' as const,
    },
    name: {
      fontWeight: 600,
      fontSize: '13px',
      color: '#111827',
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    },
    wins: {
      color: '#22c55e',
      fontWeight: 600,
      fontSize: '13px',
      marginLeft: '12px',
      flexShrink: 0,
    },
    spinner: {
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid #2563eb',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      animation: 'spin 1s linear infinite',
    },
    centered: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '150px',
    },
    errorText: {
      color: '#ef4444',
      fontSize: '14px',
      textAlign: 'center' as const,
    },
    emptyText: {
      color: '#94a3b8',
      fontSize: '14px',
      textAlign: 'center' as const,
    },
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>Top 10 — {mode}</h3>
          <button style={styles.closeButton} onClick={onClose}>&times;</button>
        </div>
        <div style={styles.body}>
          {loading ? (
            <div style={styles.centered}>
              <div style={styles.spinner} />
            </div>
          ) : error ? (
            <div style={styles.centered}>
              <p style={styles.errorText}>{error}</p>
            </div>
          ) : performers.length === 0 ? (
            <div style={styles.centered}>
              <p style={styles.emptyText}>No performers found for this period</p>
            </div>
          ) : (
            performers.map((performer, index) => (
              <div
                key={`${performer.userId || performer.name}-${performer.mode}-${index}`}
                style={styles.row}
                onClick={() => performer.userId && onViewPlayer?.(performer.userId)}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
              >
                <span style={styles.rank}>#{index + 1}</span>
                {(() => {
                  const rankColors = getRankColor(performer.rankName || '');
                  return (
                    <span style={{ ...styles.rankBadge, background: rankColors.bg, color: rankColors.color }}>
                      {performer.rankName || 'Beginner'}
                    </span>
                  );
                })()}
                <span style={styles.name}>{performer.name}</span>
                <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '8px', flexShrink: 0 }}>
                  {performer.totalPlays ?? 0} played
                </span>
                <span style={styles.wins}>{performer.wins} wins</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
